import type { ColorRoles } from '@hapana/shared/web/styles/material-ui/color-roles'

import { figmaPrimitives as p, toDarkShades } from './figma-primitives'
import { palette } from './palette'

const n = p.neutral

export const darkColorRoles = {
  surface: {
    page: n[950],
    default: n[900],
    strong: n[850],
    hover: n[800],
    inverse: n[0],
    overlay: 'rgba(0, 0, 0, 0.6)',
    muted: 'rgba(0, 0, 0, 0.3)',
  },
  border: {
    subtle: n[800],
    default: n[700],
    strong: n[600],
  },
  text: {
    primary: n[50],
    secondary: n[200],
    disabled: n[600],
    inverse: n[900],
    onPrimary: n[900],
  },
  status: {
    neutral: { background: n[850], text: n[400] },
    danger: { background: p.red[800], text: p.red[400] },
    warning: { background: p.orange[800], text: p.orange[400] },
    success: { background: p.green[800], text: p.green[400] },
  },
} satisfies ColorRoles

export const dark_palette = {
  ...darkColorRoles,
  mode: 'dark' as const,
  background: {
    default: n[950],
    paper: n[900],
  },
  divider: n[800],
  primary: { ...toDarkShades(p.brand), contrastText: n[900] },
  secondary: toDarkShades(p.purple),
  overlays: {
    default: 'rgba(0, 0, 0, 0.6)',
    dark: n[600],
  },
  gradient: palette.gradient,
  info: {
    darkest: n[50],
    dark: n[100],
    main: n[300],
    light: n[800],
    lightest: n[850],
  },
  blue: {
    lightest: p.location[800],
    dark: p.location[400],
  },
  yellow: {
    lightest: p.orange[800],
    dark: p.orange[300],
  },
  green: {
    dark: p.brand[400],
    main: p.brand[500],
    lightest: p.brand[800],
  },
  orange: {
    dark: p.orange[400],
    lightest: p.orange[800],
  },
  success: toDarkShades(p.green),
  warning: toDarkShades(p.orange),
  error: toDarkShades(p.red),
  gray: {
    0: n[900],
    3: n[950],
    8: n[850],
    12: n[800],
    16: n[700],
    40: n[600],
    56: n[500],
    64: n[400],
    80: n[200],
    100: n[50],
  },
  grey: {
    50: n[950],
    100: n[900],
    200: n[850],
    300: n[800],
    400: n[700],
    500: n[600],
    600: n[500],
    700: n[400],
    800: n[300],
    900: n[100],
    A100: n[800],
    A200: n[700],
    A400: n[500],
    A700: n[300],
  },
  custom: {
    turquoise: {
      main: p.brand[500],
      lightest: p.brand[800],
      dark: p.brand[400],
    },
    navy: {
      deep: n[50],
    },
    blue: {
      light: p.location[700],
      main: p.location[400],
    },
    table: {
      main: n[800],
    },
    button: {
      hover: n[800],
    },
    user: {
      header: n[850],
      title: p.brand[300],
      subtitle: n[200],
      action: p.brand[400],
      background: n[900],
    },
    attendanceStatus: {
      booked: {
        main: p.teal[400],
        background: p.teal[800],
      },
      attended: {
        main: p.green[400],
        background: p.green[800],
      },
      noShow: {
        main: p.red[400],
        background: p.red[800],
      },
      lateCancelled: {
        main: p.location[400],
        background: p.location[800],
      },
    },
  },
  content: palette.content,
  alertmessage: {
    iconColor: p.orange[300],
  },
  pink: {
    lightest: p.pink[800],
    dark: p.pink[400],
  },
  icon: {
    teal: p.teal[400],
    green: p.green[400],
    orange: p.orange[400],
    red: p.red[400],
    purple: p.purple[400],
    blue: p.location[400],
    light: n[500],
    default: n[200],
  },
  alert: {
    danger: {
      lightest: p.red[800],
      dark: p.red[400],
    },
    teal: {
      lightest: p.teal[800],
      dark: p.teal[400],
    },
    successV2: {
      lightest: p.green[800],
      dark: p.green[400],
    },
    indigo: {
      lightest: p.purple[800],
      dark: p.purple[400],
    },
    info: {
      background: p.location[800],
      border: p.location[700],
      text: p.location[400],
      icon: p.location[500],
    },
  },
  aggregator: palette.aggregator,
  attendance: {
    site: p.purple[400],
    total: p.orange[400],
  },
  link: {
    main: p.location[300],
  },
  pdf: palette.pdf,
  paymentStatus: {
    paid: p.green[400],
    processing: p.orange[300],
    failed: p.red[400],
  },
  tabs: {
    enabledLocation: p.location[500],
    default: n[200],
  },
  badge: {
    brand: { background: p.brand[800], text: p.brand[400] },
    location: {
      background: p.location[700],
      text: p.location[300],
      icon: p.location[400],
      border: p.location[600],
    },
    purple: { background: p.purple[800], text: p.purple[400] },
    pink: { background: p.pink[800], text: p.pink[400] },
    warning: { background: p.orange[800], text: p.orange[400] },
    success: { background: p.green[800], text: p.green[400] },
    neutral: { background: n[800], text: n[400] },
  },
}

export const dark_settings_palette = {
  ...dark_palette,
  primary: { ...toDarkShades(p.location), contrastText: n[900] },
  custom: {
    ...dark_palette.custom,
    chip: {
      background: p.location[800],
      text: p.location[400],
      main: 'rgba(255, 255, 255, 0.6)',
    },
    user: {
      ...dark_palette.custom.user,
      action: p.location[400],
    },
  },
}
